import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { Mechanics } from "game-common/mechanics/mechanics"
import { contentTexture } from "./content_textures"
import { CompositeTilemap } from "@pixi/tilemap"
import { MapDelta, TileEffect } from "game-common/map/map"
import { Coordinate, Entity, EntityId, TileMap, TileSize, Visibility } from "game-common/models"
import { ResettableIterator, roughToFineCoordinates } from "game-common/util"
import { Container, Sprite, Texture, IPointData, Graphics, Rectangle, filters } from "pixi.js"
import { logger } from "../../../logger"
import { HoleEffect } from "./effects/hole_effect"
import {
    ITileEffectSystem,
    SurfaceObject,
    TileEffectSystem,
    TileEffectSystemConfig,
} from "./effects/tile_effect_system"

import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { ClientGameLogic } from "../../../client_game_logic"
import { IMapRenderingManager, RenderResult } from "../../../client_models"
import { CameraManagementService } from "./camera_management_service"
import { CompositeTileRenderer } from "./composite_tile_renderer"
import { MinimapSurface } from "./minimap_surface"
import { ScreenShakeManager } from "./screen_shake_manager"
import { ISkyManager, SkyManager, SkyManagerConfig } from "./sky_manager"
import { SpriteManagementService } from "./sprite_management_service"
import { spriteContentWorldBounds } from "./sprites/texture_content_bounds"
import { TileTextureExtractor } from "./sprites/tile_texture_extractor"
import { WorldOverlaySurface } from "./world_overlay_surface"
import { GrassSway } from "./effects/grass_sway"
import { spritedTileAnchorY, spritedTileZIndex } from "./sprited_tile_zindex"
import { EntityTileOccluderFade } from "./entity_tile_occluder_fade"
import { LavaTileFade } from "./effects/lava_tile_fade"
import { wallGrassFringeTexture } from "./effects/wall_grass_fringe"
import { SceneRoomRenderer } from "./scene_room_renderer"
import { RenderedTileOcclusion } from "./rendered_tile_occlusion"
import { VisibilityFade } from "./visibility_fade"
import { isExteriorFloorFall } from "game-common/floor_fall"
import type { EntityRenderingManager } from "./entity_rendering_manager"
import { cellKey, hasHouseRoof, houseRoofOffset, insideHouse, roofCellsForHouse, STOREY_RISE_PX, ROOF_UPLIFT_PX } from "game-common/room/ember/two_storey_house"
import { isRelatedFloorTransfer } from "../../../related_room_transfer"
import { roofVisibleBounds } from "game-common/room/ember/roof_support"
import { stairExitLanding } from "game-common/room/ember/stairs"
import { applyStairOrientation } from "./stair_orientation"
import { buildHouseRoof } from "game-common/room/ember/house_roof"
import { tileSetSpecs } from "game-common/room/base_map_builder"
const seedrandom = require("seedrandom")

interface CaptureGroup {
    x: number
    y: number
    tile: string
    context?: any
    delta?: MapDelta
}

export class MapRenderingManager implements IMapRenderingManager {
    // Bends the room's grass in the wind (GrassSway). Optional: a renderer that does not want the
    // effect, or a test, simply never sets one, and every call site is guarded. The blades themselves
    // come from the COMPOSITE tile path, not from the sprited-layer cells below -- long grass is a
    // two-tile composite -- so this is handed to the CompositeTileRenderer rather than used here.
    private grassSway: GrassSway | undefined

    private surface: Container
    private worldOverlaySurface: WorldOverlaySurface
    private skySurface: Container
    private tilemapRenderers: ResettableIterator<CompositeTilemap>
    private tilemapRendererList: CompositeTilemap[]
    private lavaTileFade = new LavaTileFade()
    private lavaFadeSurfaces = new Map<CompositeTilemap, Container>()
    private tilemapMetaProvider: TileMapMetaProvider
    private visibility?: Visibility
    private needsRender: boolean = false
    private miniMapSurface: MinimapSurface
    private screenShakeManager: ScreenShakeManager
    private compositeTileRenderer: CompositeTileRenderer
    private compositeTileSpriteTextures: Map<string, Texture> = new Map()
    // Sprited-layer sprites owned per-cell (key `${x}-${y}-${layerId}`), NOT drawn from the frame
    // sprite pool -- so a single cell can be repainted incrementally on a map delta / redrawClient
    // without the LIFO pool's all-or-nothing release. Destroyed on full render + incremental removal.
    private spritedCellSprites: Map<string, Sprite[]> = new Map()
    // Which layer ids have actually rendered sprited cells, so a per-cell lookup can check just those
    // keys instead of scanning the whole map's worth of them. Populated as cells render.
    private spritedLayerIds: Set<string> = new Set()
    // Fades map tile sprites (tree/bush canopies, cave ore blocks) the local player stands behind
    // toward translucent, so the player is never fully hidden by them. Alpha-only; see applyOccluderFade.
    private occluderFade: EntityTileOccluderFade = new EntityTileOccluderFade()
    // The surfaceId (if any) each sprited cell registered its owned sprites under, so an incremental
    // remove can detach those exact sprites from the shared surfaceObjectsByKey spriteSet (which the
    // tile-effect system reads). Without this, removeSpritedCell destroys the sprites but leaves dead
    // references in the spriteSet, and a later hit reads `.x`/`.texture` on a destroyed sprite -> throw.
    private spritedCellSurfaceId: Map<string, string> = new Map()
    // Cells (key `${x}-${y}-${layerId}`) changed by applied map deltas since the last repaint. On a
    // redrawClient signal we repaint just these incrementally (sprited layers) instead of the whole
    // map; if any dirty cell is a tilemap/other layer we fall back to a full rerender. Cleared on
    // both paths.
    private dirtyCells: Set<string> = new Set()
    private pendingRedraw: boolean = false
    // Set when a dirty batch includes a removal or a damaged (hp < hpMax) tile. Those cells must go
    // through the FULL rerender path: the tile-effect system (crumble, wall shake, damage holes) owns
    // and reads these sprites, and an incremental remove+recreate races/breaks it. Pristine placements
    // (new tile, hp == hpMax / no hp) stay incremental.
    private dirtyNeedsFull: boolean = false
    private skyManager: ISkyManager
    private tileEffectSystem: ITileEffectSystem
    private surfaceObjectsByKey: Map<string, SurfaceObject> = new Map()
    private cameraManagementService: CameraManagementService
    private spriteOcclusionBounds = new Rectangle()
    private renderedOcclusionTiles = new RenderedTileOcclusion()
    private sceneRenderer: SceneRoomRenderer
    private houseRoof = new CompositeTilemap()
    private houseRoofDepth = new Graphics()
    private houseFloorDepth = new Graphics()
    private houseRoofCells = new Set<string>()
    private viewEntity?: Entity
    setViewEntity = (entity: Entity) => { this.viewEntity = entity }
    private houseRoofCutawayAllowed = true
    private houseRoofFade = new VisibilityFade()
    private projectionFloor?: number
    private renderedStoreyRisePx = STOREY_RISE_PX
    private renderedRoofUpliftPx = ROOF_UPLIFT_PX
    private houseRoofFallback = new Map<string, string>()

    private roomId: EntityId
    private RANDOM_NUMBER_GENERATOR
    private spriteManagementService: SpriteManagementService
    private rngCache: Map<string, number> = new Map()
    private logic: ClientGameLogic

    // Rendering state lock and queue system for race condition prevention
    private isRendering: boolean = false
    private pendingMapDeltas: MapDelta[] = []
    private pendingRerender: boolean = false

    constructor(
        surface: Container,
        skySurface: Container,
        miniMapSurface: MinimapSurface,
        worldOverlaySurface: WorldOverlaySurface,
        logic: ClientGameLogic,
    ) {
        this.surface = surface
        this.surface.addChild(this.houseRoofDepth, this.houseFloorDepth, this.houseRoof)
        this.houseFloorDepth.zIndex = -0.5
        this.houseRoofDepth.interactive = this.houseFloorDepth.interactive = false
        // Soften only the depth cues; the pixel artwork remains crisp.
        this.houseRoofDepth.filters = [new filters.BlurFilter(1.25, 2)]
        this.houseFloorDepth.filters = [new filters.BlurFilter(1.25, 2)]
        this.sceneRenderer = new SceneRoomRenderer(surface, (root, sky) => {
            const map = new MapRenderingManager(root, sky, { update: () => {} } as unknown as MinimapSurface,
                new WorldOverlaySurface(logic, root), logic)
            // Companion rooms are seen from another floor. The active player's coordinates
            // must never open a cutaway in the downstairs roof when viewed from upstairs.
            map.setHouseRoofCutawayAllowed(false)
            map.init()
            return map
        }, entity => (logic.clientRenderer.entity() as EntityRenderingManager).renderSceneEntity(entity),
            (entity, sprite) => (logic.clientRenderer.entity() as EntityRenderingManager).retainLevelEntity(entity, sprite),
            id => logic.getEmberRole(id))
        this.skySurface = skySurface
        this.miniMapSurface = miniMapSurface
        this.worldOverlaySurface = worldOverlaySurface
        this.screenShakeManager = new ScreenShakeManager()
        this.logic = logic

        // Initialize camera management service
        this.cameraManagementService = new CameraManagementService({
            surface: this.surface,
            worldOverlaySurface: this.worldOverlaySurface,
            screenShakeManager: this.screenShakeManager,
        })

        // Initialize sprite management service
        this.spriteManagementService = new SpriteManagementService({
            spritePoolSize: 2000,
            defaultSurface: this.surface,
        })
        // Initialize sky manager
        const skyConfig: SkyManagerConfig = {
            skySurface: this.skySurface,
            tilemapMetaProvider: null as any, // Will be updated in update method
        }
        this.skyManager = new SkyManager(skyConfig)

        // Initialize composite tile renderer first
        this.compositeTileRenderer = new CompositeTileRenderer({
            surface: this.surface,
            skySpriteSurfaceAbove: null as any, // Will be set after sky manager is created
            tilemapMetaProvider: null as any, // Will be updated in update method
            compositeTileSpriteTextures: this.compositeTileSpriteTextures,
            surfaceObjectsByKey: this.surfaceObjectsByKey,
            attachTexture: this.spriteManagementService.attachTexture,
            getGrassSway: () => this.grassSway,
            pertubation: this.pertubation,
            pctChance: this.pctChance,
            logic: this.logic,
            tileTextureExtractor: TileTextureExtractor.instance,
        })

        // Initialize tile effect system
        const tileEffectConfig: TileEffectSystemConfig = {
            surface: this.surface,
            worldOverlaySurface: this.worldOverlaySurface,
            tilemapMetaProvider: null as any, // Will be updated in update method
            holeEffect: new HoleEffect("", () => Math.random()),
            skySpriteSurfaceAbove: null as any, // Will be set after sky manager is created
            skySpriteSurfaceBelow: null as any, // Will be set after sky manager is created
            attachTexture: this.spriteManagementService.attachTexture,
            renderCompositeTile: this.renderCompositeTile,
            handleRenderCompositeTile: this.compositeTileRenderer.handleRenderCompositeTile,
        }
        this.tileEffectSystem = new TileEffectSystem(tileEffectConfig, this.surfaceObjectsByKey)
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    init = () => {
        this.tilemapRendererList = [
            new CompositeTilemap(),
            new CompositeTilemap(),
            new CompositeTilemap(),
            new CompositeTilemap(),
            new CompositeTilemap(),
            new CompositeTilemap(),
        ]
        this.tilemapRenderers = new ResettableIterator<CompositeTilemap>(this.tilemapRendererList)
        this.tilemapRendererList.forEach((renderer, i) => {
            this.surface.addChild(renderer)
            // Same ground sort order, immediately above this layer and below the next one.
            const fades = new Container()
            this.surface.addChild(fades)
            this.lavaFadeSurfaces.set(renderer, fades)
        })

        // Update composite tile renderer with sky surfaces from SkyManager
        this.compositeTileRenderer.updateSkySurfaces(this.skyManager.getSkySpriteSurfaceAbove())

        // Update tile effect system with sky surfaces from SkyManager
        this.tileEffectSystem.updateSkySurfaces(
            this.skyManager.getSkySpriteSurfaceAbove(),
            this.skyManager.getSkySpriteSurfaceBelow(),
        )

        // Live tuning hook: window.occluderFade.enabled / .alpha / .speed / .minNeighbors etc.
        // (matches window.deathEffect).
        if (typeof window !== "undefined") {
            ;(window as any).occluderFade = this.occluderFade.config
        }
    }

    // ============================================================================
    // CORE RENDERING METHODS
    // ============================================================================

    renderTileMap = () => {
        if (this.renderedStoreyRisePx !== STOREY_RISE_PX || this.renderedRoofUpliftPx !== ROOF_UPLIFT_PX) {
            this.renderedStoreyRisePx = STOREY_RISE_PX
            this.renderedRoofUpliftPx = ROOF_UPLIFT_PX
            this.rerender()
        }
        if (this.houseRoofCutawayAllowed) this.skyManager.updatePlayerLocation(this.viewEntity ?? this.logic.playerEntity)
        this.sceneRenderer.render(this.tilemapMetaProvider?.getHousePresentation(), this.viewEntity ?? this.logic.playerEntity)
        const house = this.tilemapMetaProvider?.getHousePresentation()
        // A live roof can be added to an open top floor without a room reload.
        this.houseRoof.y = hasHouseRoof(house) ? houseRoofOffset(house, this.projectionFloor ?? house.floor) : 0
        this.houseRoofDepth.y = this.houseRoof.y
        this.houseRoof.zIndex = house ? 100 + (house.top + house.depth) * TileSize - 1 : 0
        this.houseRoofDepth.zIndex = this.houseRoof.zIndex - 0.5
        this.houseRoofFade.update(hasHouseRoof(house) && (!this.houseRoofCutawayAllowed ||
            isExteriorFloorFall((this.viewEntity ?? this.logic.playerEntity)?.floorFall) ||
            (!!(this.viewEntity ?? this.logic.playerEntity)?.location && !insideHouse(house, (this.viewEntity ?? this.logic.playerEntity).location))))
        this.houseRoof.alpha = this.houseRoofDepth.alpha = this.houseRoofFade.alpha
        this.houseRoof.visible = this.houseRoofFade.visible
        this.houseRoofDepth.visible = this.houseRoof.visible
        if (this.lavaTileFade.update()) this.needsRender = true
        // Apply queued map deltas every frame, independent of the visual re-render gate
        // below. Deltas carry collision/blocking changes -- e.g. a chopped-down tree
        // becoming walkable. Previously deltas were only applied inside a full re-render,
        // but a tree cut does not trigger one (it sends a map delta, not a redrawClient),
        // so the client's collision map (tileTypesMap "blocking") stayed stale until the
        // next unrelated re-render or a reload. The player then predicts a collision the
        // server no longer enforces; reconciliation repeatedly yanks the player through the
        // now-empty space -- the shudder when walking where a tree used to be. Applying
        // deltas here mutates the persistent data model, so a later full re-render still
        // reflects them.
        this.processPendingDeltas()

        // A redrawClient signal: repaint just the delta-changed cells (incremental) rather than the
        // whole map. Runs here, after processPendingDeltas has applied the accompanying deltas. May
        // escalate to a full rerender (needsRender) if a tilemap-layer cell changed.
        if (this.pendingRedraw && !this.isRendering) {
            this.pendingRedraw = false
            this.repaintDirtyCells()
        }

        if (!this.shouldRender()) {
            return
        }

        this.isRendering = true
        try {
            this.prepareRendering()
            this.renderAllLayers()
            this.finalizeRendering()
        } finally {
            this.isRendering = false
        }

        // Process queued re-render request
        if (this.pendingRerender) {
            this.pendingRerender = false
            this.needsRender = true
        }
    }

    renderAt = (x: number, y: number, tilemap: TileMap, layerId: string, options?: any) => {
        let { tilemapRender, wasReplaced, surfaceId } = options || {}

        tilemapRender = tilemapRender || this.tilemapRenderers.next().value
        const size = TileSize
        const _x = tilemap[x]
        if (!_x) {
            return
        }
        let spriteToReturn
        const tile = _x[y]
        if (tile && this.tilemapMetaProvider.shouldDrawTileAt(x, y, layerId, tilemap)) {
            if (!this.tilemapMetaProvider.isHiddenTile(tile)) {
                const compositeTileId = this.tilemapMetaProvider.getCompositeTileId(tile)
                if (compositeTileId) {
                    this.renderCompositeTile(this.tilemapMetaProvider, tile, compositeTileId, x, y)
                } else {
                    const tilesetTileId = this.tilemapMetaProvider.resolveTilesetTileId(tile)
                    if (layerId === "sky" && tilemap === this.tilemapMetaProvider.resolveTileMapFromLayerId("sky") &&
                        this.houseRoofCells.has(`${x},${y}`)) {
                        // The house roof is painted independently of the sky pass, beneath
                        // the upper room. Ordinary sky visibility must not control it.
                        return
                    }
                    if (layerId) {
                        const layerMapping = this.tilemapMetaProvider.resolveLayerMapping(layerId)
                        if (layerMapping.maptype === "sprited") {
                            spriteToReturn = this.spriteManagementService.createSpriteAt(tilesetTileId, x, y)
                        }
                        if (layerMapping.maptype === "tilemap") {
                            this.renderGroundTile(this.tilemapRenderers.values[layerMapping.idx], tilesetTileId, x, y, layerId)
                            if (wasReplaced) {
                                // todo -- this is too blunt... maybe there is a way to avoid full
                                // rerenders on pixijs tilemap renders
                                this.rerender()
                                return
                            }
                        }
                        if (layerMapping.maptype === "sky") {
                            const delta = this.tilemapMetaProvider.getMapDeltaAt(x, y, "sky")
                            if (delta?.collideable) {
                                spriteToReturn = this.spriteManagementService.createSpriteAt(
                                    tilesetTileId,
                                    x,
                                    y,
                                    0,
                                    this.skyManager.getSkySpriteSurfaceAbove(),
                                )
                                spriteToReturn.y += houseRoofOffset()
                            } else {
                                this.skyManager.getSkyTilemapRenderer().tile(`${tilesetTileId}`, x * size, y * size + houseRoofOffset())
                            }
                        }
                    } else {
                        this.renderGroundTile(tilemapRender, tilesetTileId, x, y, options?.fadeLayerId)

                        // Create overlay sprite for damage effects on regular layers
                        const delta = this.tilemapMetaProvider.findMapDeltaAt(x, y)
                        if (this.tileEffectSystem.shouldCreateDamageOverlay(delta)) {
                            spriteToReturn = this.tileEffectSystem.createDamageOverlaySprite(tilesetTileId, x, y)
                        }
                    }
                }
            }
        }

        if (spriteToReturn && layerId === "sprite2") {
            this.orientStairSprite(spriteToReturn, x, y, tile)
        }
        return spriteToReturn
    }

    private orientStairSprite = (sprite: Sprite, x: number, y: number, tile: string): void => {
        const type = this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId
        if (type !== "stairs-up" && type !== "stairs-down") return
        applyStairOrientation(sprite, { x, y }, type, stairExitLanding(this.tilemapMetaProvider, { x, y }))
    }

    private renderGroundTile = (renderer: CompositeTilemap, texture: string, x: number, y: number, layerId?: string): void => {
        if (!renderer) return
        const fadeSurface = this.lavaFadeSurfaces.get(renderer)
        if (fadeSurface && layerId && this.lavaTileFade.render(`${x}-${y}-${layerId}`, `${texture}`, x, y, fadeSurface)) return
        renderer.tile(`${texture}`, x * TileSize, y * TileSize)
        if (!this.houseRoofCutawayAllowed) {
            const art = contentTexture(texture)
            this.renderedOcclusionTiles.add(renderer,
                new Rectangle(x * TileSize, y * TileSize, art.width, art.height))
        }
    }

    addTileAt = (x: number, y: number, tile: string, layerId: string, owned = false): RenderResult => {
        const mapping = this.tilemapMetaProvider.resolveLayerMapping(layerId)
        const tilesetTileId = this.tilemapMetaProvider.resolveTilesetTileId(tile)
        // Pending build ghosts must survive map redraws without being reused by the frame pool.
        const sprite = owned ? new Sprite(contentTexture(tilesetTileId)) :
            this.spriteManagementService.createSpriteAt(tilesetTileId, x, y, mapping?.idx || 0)
        if (owned) {
            sprite.position.set(x * TileSize, y * TileSize)
            sprite.zIndex = 100 + (mapping?.idx || 0) + y * TileSize + TileSize / 3
            this.surface.addChild(sprite)
        }
        if (mapping?.maptype === "sky") sprite.y += houseRoofOffset()
        if (layerId === "sprite2") {
            this.orientStairSprite(sprite, x, y, tile)
        }

        return {
            sprite,
        }
    }

    updateCompositeTilePointer = (at: IPointData) => this.compositeTileRenderer.updateCompositeTilePointer(at)

    renderCompositeTile = (
        tilemapMetaProvider: TileMapMetaProvider,
        parentTileId: string,
        compositeTileId: string,
        x: number,
        y: number,
        zIndex: number = 0,
    ) => {
        this.compositeTileRenderer.renderCompositeTile(tilemapMetaProvider, parentTileId, compositeTileId, x, y, zIndex)
    }

    // ============================================================================
    // PLAYER/CAMERA MANAGEMENT METHODS
    // ============================================================================

    updatePlayerLocation = (entity: Entity, force?: boolean) => {
        if (!entity) {
            return
        }

        // Update sky visibility based on player position
        this.skyManager.updatePlayerLocation(entity, force)

        if (
            (!force && this.needsRender) ||
            !this.tilemapMetaProvider ||
            entity.location.x < 0 ||
            entity.location.y < 0
        ) {
            return
        }

        // Delegate camera positioning to camera management service
        this.cameraManagementService.updatePlayerLocation(entity, force)
    }

    screenShakeImpulse = (entity: Entity, amplitude: number, durationMs: number) => {
        this.cameraManagementService.screenShakeImpulse(entity, amplitude, durationMs)
    }

    screenShake = (entity: Entity) => {
        this.cameraManagementService.screenShake(entity)
    }

    // ============================================================================
    // EFFECTS AND OVERLAYS METHODS
    // ============================================================================

    updateTileEffects = () => this.tileEffectSystem.update()

    predictTreeStrike = (location: Coordinate, entity: Entity) => {
        const target = this.tilemapMetaProvider?.getEntityTileAt(location.x, location.y)
        if (!target || Mechanics.entityTile.hpDecrementOnHit(target.entityTile, entity.weapon?.weaponType || "none") < 1) return
        this.tileEffectSystem.predictTreeStrike({
            effectType: "damage", mapRoughCoordinates: location, ...target,
            surfaceId: this.tilemapMetaProvider.findMapDeltaAt(location.x, location.y)?.context?.surfaceId,
            strikerId: entity.entityId, strikeFrom: entity.location,
            strikeStrength: !entity.weapon || entity.weapon.weaponType === "none" ? 0.35 : entity.weapon.weaponType === "warhammer" ? 1.5 : 1,
        })
    }

    tileEffect = (effect: TileEffect) => {
        this.tileEffectSystem.applyTileEffect(effect)
    }

    overlay = () => this.worldOverlaySurface

    setStaticHidingAllowed = (allowed: boolean) => this.skyManager.setStaticHidingAllowed(allowed)
    setProjectionFloor = (floor: number) => { this.projectionFloor = floor }

    get worldProjectionOffset(): number { return this.surface.pivot.y }

    private updateHouseProjection = (): void => {
        // Choose the world origin when loading the active map. Adding the first stairs
        // introduces house metadata mid-room; changing the pivot then jumps the camera.
        // Passive floors inherit the active world's lift through their parent surface.
        const offset = this.projectionFloor === undefined && this.tilemapMetaProvider?.getHousePresentation()
            ? TileSize : 0
        this.surface.pivot.y = offset
        this.worldOverlaySurface.pivot.y = offset
    }
    setHouseRoofCutawayAllowed = (allowed: boolean) => { this.houseRoofCutawayAllowed = allowed }

    // ============================================================================
    // MINIMAP METHODS
    // ============================================================================

    updateMinimap = (tilemapMetaProvider: TileMapMetaProvider) => {
        this.miniMapSurface.update(tilemapMetaProvider)
    }

    updateBiomeColorMap = (biomeCoordinate: Coordinate, color: number) => {
        this.miniMapSurface.updateBiomeColorMap(biomeCoordinate, color)
    }

    removeLocationIcon = (iconId: string) => {
        this.miniMapSurface.removeLocationIcon(iconId)
    }

    // ============================================================================
    // STATE MANAGEMENT METHODS
    // ============================================================================

    update = (tilemapMetaProvider: TileMapMetaProvider, visibility?: Visibility) => {
        if (tilemapMetaProvider) {
            const previousHouse = this.tilemapMetaProvider?.twoStoreyHouse
            const nextHouse = tilemapMetaProvider.twoStoreyHouse
            const linkedFloor = isRelatedFloorTransfer(this.roomId, previousHouse, tilemapMetaProvider.roomId, nextHouse)
            this.tilemapMetaProvider = tilemapMetaProvider
            this.updateHouseProjection()
            if (this.roomId !== tilemapMetaProvider.roomId) {
                if (linkedFloor) this.sceneRenderer.activateRoom(tilemapMetaProvider.roomId)
                else this.sceneRenderer.clear()
                this.houseRoofFade.reset()
            }
            const house = tilemapMetaProvider.getHousePresentation()
            this.houseRoofCells = new Set(hasHouseRoof(house) ? roofCellsForHouse(house).map(cellKey) : [])
            this.houseRoofFallback.clear()
            if (hasHouseRoof(house)) {
                buildHouseRoof(roofCellsForHouse(house), tilemapMetaProvider.width(), tilemapMetaProvider.height(), tilemapMetaProvider.roomId)
                    .filter(entry => entry.layerId === "sky").forEach(entry => this.houseRoofFallback.set(`${entry.x},${entry.y}`,
                        String(tileSetSpecs[entry.tileSpec.tilesetId].firstgid + entry.tileSpec.tilePos)))
            }
            this.houseRoof.y = hasHouseRoof(house) ? houseRoofOffset(house, this.projectionFloor ?? house.floor) : 0
            this.houseRoof.zIndex = house ? 100 + (house.top + house.depth) * TileSize - 1 : 0
            this.houseRoofDepth.y = this.houseRoof.y
            this.houseRoofDepth.zIndex = this.houseRoof.zIndex - 0.5
            // Update composite tile renderer with new tilemap meta provider
            this.compositeTileRenderer.updateTilemapMetaProvider(tilemapMetaProvider)
            // Update tile effect system with new tilemap meta provider
            this.tileEffectSystem.updateTilemapMetaProvider(tilemapMetaProvider)
            // Update sky manager with new tilemap meta provider
            this.skyManager.updateTilemapMetaProvider(tilemapMetaProvider)
            if (this.roomId !== tilemapMetaProvider.roomId) {
                this.lavaTileFade.clear()
                this.roomId = tilemapMetaProvider.roomId
                // new room boundary has been crossed
                this.tileEffectSystem.clearSurfaceObjects()
                this.RANDOM_NUMBER_GENERATOR = seedrandom(this.roomId)
                this.rngCache.clear()
                // Reset sky state
                if (!linkedFloor) this.skyManager.resetSkyState()
                // Update tile effect system with new room context
                this.tileEffectSystem.updateHoleEffectContext(this.roomId, this.RANDOM_NUMBER_GENERATOR)
            }
            this.rerender()
            this.cameraManagementService.updateTranslationComputer(tilemapMetaProvider)
        }
        this.visibility = visibility
        if (this.visibility) {
            this.visibility.lastUpdateTs = Date.now()
        }
    }

    /**
     * Does the map still owe the screen a paint? True from the moment a room (or a redraw) is handed
     * over until the next frame has drawn it. The room-reveal hold asks, because the ground is the
     * one thing that is certainly not ready on the frame a room lands (see RoomReveal).
     */
    hasPendingRender = (): boolean => {
        return this.needsRender || this.pendingRerender
    }

    rerender = () => {
        if (this.isRendering) {
            // Queue the re-render request for after current rendering completes
            this.pendingRerender = true
        } else {
            this.needsRender = true
        }
    }

    reset = () => {
        this.cameraManagementService.reset()
    }

    /**
     * Clean up resources and cancel pending operations
     */
    public destroy = (): void => {
        this.renderedOcclusionTiles.clear()
        this.sceneRenderer.clear()
        this.houseRoof.destroy({ children: true })
        for (const depth of [this.houseRoofDepth, this.houseFloorDepth]) {
            depth.filters?.forEach(filter => filter.destroy())
            depth.filters = null
            depth.destroy()
        }
        this.lavaTileFade.clear()
        this.lavaFadeSurfaces.forEach(surface => surface.destroy({ children: true }))
        this.lavaFadeSurfaces.clear()
        // Clean up sprite management service
        this.spriteManagementService.destroy()
    }

    // ============================================================================
    // UTILITY/HELPER METHODS
    // ============================================================================

    randomIntBetweenRng = (min: number, max: number, seed?: string) => {
        let randomFloat
        if (!seed) {
            randomFloat = this.RANDOM_NUMBER_GENERATOR ? this.RANDOM_NUMBER_GENERATOR() : Math.random()
        } else {
            const toUse = `${seed}-${min}-${max}-${this.roomId}`
            randomFloat = this.rngCache.get(toUse)
            if (randomFloat === undefined) {
                randomFloat = this.RANDOM_NUMBER_GENERATOR ? this.RANDOM_NUMBER_GENERATOR() : Math.random()
                this.rngCache.set(toUse, randomFloat)
            }
        }

        return Math.floor(randomFloat * (max - min + 1) + min)
    }

    pctChance = (pct: number, seed?: string) => {
        // min and max included
        return this.randomIntBetweenRng(0, 100, seed) >= 100 - pct
    }

    pertubation = (tileId: string, x: number, y: number, offset: number, type: string, range: number = 5) => {
        const key = `${type}-${tileId}-${x}-${y}-${range}-${offset}`
        return this.randomIntBetweenRng(-range, range, key)
    }

    // ============================================================================
    // PRIVATE HELPER METHODS
    // ============================================================================

    private shouldRender = (): boolean => {
        return this.needsRender && !!this.tilemapMetaProvider
    }

    private prepareRendering = (): void => {
        this.renderedOcclusionTiles.clear()
        this.houseRoof.clear()
        this.houseRoofDepth.clear()
        this.houseFloorDepth.clear()
        this.tileEffectSystem.clearSurfaceObjects()
        let errorCount = 0
        const releasedCount = this.spriteManagementService.releaseAllSprites(spriteItemObject => {
            const sprite = spriteItemObject.data
            if (!sprite.parent) {
                spriteItemObject.data = new Sprite()
                errorCount++
                return true
            } else {
                sprite.parent?.removeChild(sprite)
                return false
            }
        })

        // Sprited-layer cells use OWNED sprites (not the frame pool), so releaseAllSprites above does
        // not touch them -- destroy them here before the full rebuild. children:true tears down any
        // damage-overlay children; texture defaults false so the shared spritesheet textures survive.
        this.spritedCellSprites.forEach(sprites =>
            sprites.forEach(s => {
                s.parent?.removeChild(s)
                s.destroy({ children: true })
            }),
        )
        this.spritedCellSprites.clear()
        this.spritedLayerIds.clear()
        this.spritedCellSurfaceId.clear()
        // A full rebuild releases every pooled sprite, which is every blade the wind was bending -- and
        // a released sprite is not merely dead, it is about to be handed to some other tile. Per-CELL
        // removals need no such hook (GrassSway prunes destroyed sprites itself), but discovering a
        // whole room's worth one frame at a time would mean bending sprites that now belong elsewhere.
        this.grassSway?.clear()
        this.compositeTileRenderer.clearBonecallerStones()
        // A full render repaints every cell, so any pending incremental dirty cells are subsumed.
        this.dirtyCells.clear()
        this.dirtyNeedsFull = false

        // Clear tilemap renderers
        this.lavaTileFade.clearSprites()
        this.tilemapRendererList.forEach(renderer => renderer.clear())
        this.skyManager.clearSkySurfaces()
        this.tilemapRenderers.reset()

        // Clear tile effect system surface objects
        this.tileEffectSystem.clearSurfaceObjects()

        // Composite-tile textures (TileTextureExtractor.textureCache / compositeTileSpriteBounds and
        // this.compositeTileSpriteTextures) are content-addressed by `${tilesetId}-${compositeTileId}`,
        // immutable, and safely shared across rooms, so they are an intentional PERSISTENT memo --
        // NOT cleared per rerender (regenerating each rerender was a hitch) and bounded by the number
        // of distinct composite-tile types in the loaded tilesets. These were previously ".clear()"ed
        // here, but those calls were silent no-ops -- the Maps were accessed with bracket syntax, so
        // .clear() never touched the entries; the access is now .get/.set. Do NOT clear+destroy them
        // on room change: live sprites can still reference a cached texture, and destroy() nulls
        // Texture.orig, crashing any later getBounds() ("Cannot read properties of null (reading
        // 'width')").
    }

    private renderAllLayers = (): void => {
        this.renderHouseFloorDepth()
        this.renderHouseRoof()
        const { visit } = this.tilemapMetaProvider

        visit(({ tilemap, sky, sprited, mapSpriteCaps = [], spriteFragments, index, layerId }) => {
            if (spriteFragments) {
                spriteFragments.forEach(fragment => {
                    this.tileEffectSystem.renderSpriteFragment(fragment)
                })
            } else if (sprited) {
                this.renderSpritedLayer(tilemap, mapSpriteCaps, index, layerId)
            } else {
                if (tilemap) {
                    const tilemapRender = sky
                        ? this.skyManager.getSkyTilemapRenderer()
                        : this.tilemapRenderers.next().value
                    for (let y = 0; y < this.tilemapMetaProvider.height(); y++) {
                        for (let x = 0; x < this.tilemapMetaProvider.width(); x++) {
                            this.renderAt(x, y, tilemap, sky ? "sky" : undefined, {
                                tilemapRender,
                                fadeLayerId: layerId,
                            })
                        }
                    }
                }
            }
        })
    }

    private renderHouseFloorDepth = (): void => {
        if (!(this.tilemapMetaProvider.getHousePresentation()?.floor > 0)) return
        const floor = this.tilemapMetaProvider.resolveTileMapFromLayerId("map2")
        const hasFloor = (x: number, y: number) => !!floor?.[x]?.[y] && floor[x][y] !== "0"
        // Draw beneath the actual floor tiles. Only the offset outline remains visible
        // on the roof below, including around notches and live player extensions.
        floor?.forEach((column, x) => column.forEach((tile, y) => {
            if (!tile || tile === "0") return
            this.houseFloorDepth.beginFill(0x151018, 0.18)
                .drawRect(x * TileSize + 4, y * TileSize + 6, TileSize, TileSize).endFill()
            if (!hasFloor(x, y + 1)) {
                this.houseFloorDepth.beginFill(0x302019, 0.65)
                    .drawRect(x * TileSize, (y + 1) * TileSize, TileSize, 3).endFill()
            }
        }))
    }

    private renderHouseRoof = (): void => {
        const house = this.tilemapMetaProvider.getHousePresentation()
        if (!hasHouseRoof(house)) return
        const sky = this.tilemapMetaProvider.resolveTileMapFromLayerId("sky")
        const cells = new Map(roofCellsForHouse(house).map(cell => [cellKey(cell), cell]))
        // Live roof extensions also sit at this storey's ceiling height and support the level above.
        sky?.forEach((column, x) => column.forEach((tile, y) => {
            if (tile && tile !== "0") cells.set(`${x},${y}`, { x, y })
        }))
        this.houseRoofCells = new Set(cells.keys())
        const painted = new Map<string, { x: number; y: number; tile: string }>()
        for (const { x, y } of cells.values()) {
            if (this.tilemapMetaProvider.getMapDeltaAt(x, y, "sky")?.removed) continue
            const roofTile = sky?.[x]?.[y]
            // Older prototype camps had an empty sky layer or floor art in it. Reconstruct
            // their roof with the manual builder rather than painting floor tiles overhead.
            const tile = roofTile && roofTile !== "0" && this.tilemapMetaProvider.resolveTileSet(roofTile)?.tilesetId !== "floors"
                ? roofTile : this.houseRoofFallback.get(`${x},${y}`)
            if (!tile || tile === "0") continue
            painted.set(`${x},${y}`, { x, y, tile })
        }
        for (const { x, y, tile } of painted.values()) {
            const texture = contentTexture(this.tilemapMetaProvider.resolveTilesetTileId(tile))
            this.houseRoof.tile(texture, x * TileSize, y * TileSize)
            const tileset = this.tilemapMetaProvider.resolveTileSet(tile)
            const [left, top, right, bottom] = roofVisibleBounds(tileset?.tilesetId, Number(tile) - tileset?.firstGid)
            const px = x * TileSize + left, py = y * TileSize + top
            const width = right - left, height = bottom - top
            if (!this.houseRoofCutawayAllowed) this.renderedOcclusionTiles.add(this.houseRoof, new Rectangle(px, py, width, height))
            this.houseRoofDepth.beginFill(0x151018, 0.17)
                .drawRect(px + 4, py + 6, width, height).endFill()
            // The exposed south face gives the roof thickness; never draw seams
            // across connected interior tiles. Respect the wooden art's alpha bounds.
            if (bottom < TileSize || !painted.has(`${x},${y + 1}`)) {
                this.houseRoofDepth.beginFill(0x302019, 0.65)
                    .drawRect(px, py + height, width, 3).endFill()
            }
        }
    }

    private finalizeRendering = (): void => {
        this.miniMapSurface.update(this.tilemapMetaProvider)
        this.needsRender = false
        this.tileEffectSystem.applyDamageVisualizationToExistingTiles(this.surfaceObjectsByKey)
    }

    // Hand the map the wind that bends its grass. Set once by the renderer at construction; the map
    // passes it down to the composite tile renderer, which is what actually builds the grass, and
    // tells it when a full rebuild has taken every blade away.
    setGrassSway = (grassSway: GrassSway) => {
        this.grassSway = grassSway
    }

    // Render one sprited-layer cell as an OWNED sprite (not from the frame pool) and track it per-cell
    // in spritedCellSprites so it can be removed/repainted individually (incremental redraw). Shared
    // spritesheet textures (Texture.from) are reused; only the Sprite wrapper is owned here.
    private renderSpritedCell = (x: number, y: number, tile: string, layerId: string, index: number): void => {
        const tilesetTileId = this.tilemapMetaProvider.resolveTilesetTileId(tile)
        if (tile === "0" || !tile || this.tilemapMetaProvider.isHiddenTile(tile) || tilesetTileId === "lpc-terrain-723") {
            return
        }
        const delta = this.tilemapMetaProvider.getMapDeltaAt(x, y, layerId)
        const context = delta?.context
        const belowTile = this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId)?.[x]?.[y + 1]
        const depthItem = { x, y, context, tilesetTileId,
            belowTilesetTileId: belowTile && belowTile !== "0" ? this.tilemapMetaProvider.resolveTilesetTileId(belowTile) : undefined,
            belowBlocking: this.tilemapMetaProvider.isCollisionAt(x, y + 1) }
        const item: CaptureGroup = { x, y, tile, context, delta: !!context?.hp ? delta : undefined }
        try {
            const texture = wallGrassFringeTexture(contentTexture(tilesetTileId), tilesetTileId,
                x, y, this.tilemapMetaProvider, context?.parent)
            const g = new Sprite(texture)
            this.surface.addChild(g)
            const fine = roughToFineCoordinates({ x, y })
            g.x = fine.x
            g.y = fine.y
            // A descending stair is an opening in the floor; bodies always draw over it.
            g.zIndex = this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId === "stairs-down"
                ? 0 : spritedTileZIndex(index, spritedTileAnchorY(depthItem))
            if (layerId === "sprite2") {
                this.orientStairSprite(g, x, y, tile)
            }

            const surfaceId = context?.surfaceId
            const isParent = context?.parent && context.parent?.x === x && context.parent?.y === y
            this.tileEffectSystem.processSurfaceId(isParent ? tilesetTileId : undefined, surfaceId, g, item.delta)

            const key = `${x}-${y}-${layerId}`
            this.spritedLayerIds.add(layerId)
            const arr = this.spritedCellSprites.get(key)
            if (arr) {
                arr.push(g)
            } else {
                this.spritedCellSprites.set(key, [g])
            }
            // Remember which surface this cell registered under so removeSpritedCell can detach these
            // exact sprites from the shared spriteSet on an incremental repaint.
            if (surfaceId) {
                this.spritedCellSurfaceId.set(key, surfaceId)
            }
        } catch (e) {
            logger.error({ tags: ["map", "error"] }, e)
        }
    }

    private renderSpritedLayer = (tilemap: TileMap, mapSpriteCaps: any[], index: number, layerId: string): void => {
        // The previous per-run buffer/mapSpriteCap "splitter" machinery was vestigial: it only
        // re-assigned each sprite's z-index to the SAME per-cell value it already had (a sprited tile
        // z-sorts by its own structure base row, context.parent.y -- see sprited_tile_zindex.ts). So
        // this is now a straight per-cell render via renderSpritedCell.
        const width = this.tilemapMetaProvider.width()
        const height = this.tilemapMetaProvider.height()
        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                this.renderSpritedCell(x, y, tilemap[x][y], layerId, index)
            }
        }
    }

    // The sprited-layer sprites currently drawn at one map cell, across every sprited layer. This is
    // how a tile-drawn entity gets a hover outline: the V2 chest is an invisible marker standing on
    // these sprites, so they -- not the marker -- are the shape to trace. Returns live sprites in the
    // map surface's own coordinate space; callers must not retain them across a rerender.
    getSpritedTilesAt = (x: number, y: number): Sprite[] => {
        const found: Sprite[] = []
        this.spritedLayerIds.forEach(layerId => {
            const sprites = this.spritedCellSprites.get(`${x}-${y}-${layerId}`)
            sprites?.forEach(sprite => {
                if (!sprite.destroyed) {
                    found.push(sprite)
                }
            })
        })
        return found
    }

    // The container those sprites live in, so an outline traced from them can be parented alongside
    // and share their coordinate space without any conversion.
    getTileSurface = (): Container => this.surface

    // Remove (destroy) the owned sprites currently rendered for one sprited cell.
    private removeSpritedCell = (x: number, y: number, layerId: string): void => {
        const key = `${x}-${y}-${layerId}`
        const sprites = this.spritedCellSprites.get(key)
        if (!sprites) {
            return
        }
        // Detach these sprites from the shared surfaceObjectsByKey spriteSet BEFORE destroying them, so
        // the tile-effect system never keeps a destroyed sprite in a surface's set. A vertical wall's
        // cap+base cells share one surfaceId, so this removes only this cell's sprites and empties the
        // surface only once its last cell is removed.
        const surfaceId = this.spritedCellSurfaceId.get(key)
        if (surfaceId) {
            this.tileEffectSystem.detachSpritesFromSurface(surfaceId, sprites)
            this.spritedCellSurfaceId.delete(key)
        }
        sprites.forEach(s => {
            s.parent?.removeChild(s)
            if (!s.destroyed) {
                s.destroy({ children: true })
            }
        })
        this.spritedCellSprites.delete(key)
    }

    // The z-index "index" a sprited layer renders with (matches renderAllLayers' visit()).
    private spritedLayerIndex = (layerId: string): number => (this.tilemapMetaProvider.resolveLayerMapping(layerId)?.idx ?? 0) + 1

    hasForegroundSceneFloor(playerZ: number): boolean {
        return this.sceneRenderer.hasForegroundFloor(playerZ)
    }

    isPlayerBehindScene(bounds: Rectangle, playerZ: number): boolean {
        return this.sceneRenderer.occludes(bounds, playerZ)
    }

    /** Test individual drawn tiles, preserving openings and irregular storey footprints. */
    overlapsRenderedMap(bounds: Rectangle): boolean {
        const overlaps = (box: Rectangle) => box.x < bounds.right && box.right > bounds.x &&
            box.y < bounds.bottom && box.bottom > bounds.y
        if (this.renderedOcclusionTiles.overlaps(bounds)) return true
        const hits = (sprite: Sprite) => {
            if (sprite.destroyed || !sprite.worldVisible || !overlaps(sprite.getBounds()) || sprite.worldAlpha <= 0.15) return false
            const content = spriteContentWorldBounds(sprite, this.spriteOcclusionBounds)
            return !content || overlaps(content)
        }
        for (const sprites of this.spritedCellSprites.values()) {
            if (sprites.some(hits)) return true
        }
        const pool = this.spriteManagementService.getSpritePool()
        for (let i = 0; i <= pool.ptr; i++) {
            const item = pool.items[i]
            if (item && !item.free && item.data && hits(item.data)) return true
        }
        return false
    }

    // Fade the tile art the LOCAL player is standing behind (tree canopies, a cave's ore blocks) toward
    // translucent, and ease the rest back to opaque. Called once per frame after entities are laid out.
    // playerZIndex mirrors Mechanics.entity.body.calculateZIndexBoost for the humanoid player
    // (100 + feet-y, defaultZIndex 0) -- the same key tile sprites are z-sorted against.
    //
    // Candidates are ONLY the pooled tile sprites carrying a fade policy (occluderFade, stamped by
    // CompositeTileRenderer from the tile's structured entityTileId via occluderFadePolicy). Ground
    // tiles are baked into CompositeTilemaps and never occlude; walls (owned sprited-cell sprites) are
    // deliberately excluded, so only the tagged tiles fade.
    applyOccluderFade = (player: Entity): void => {
        this.compositeTileRenderer.updateBonecallerStones()
        // Disabled (via the OCCLUDER_FADE_ENABLED constant or a runtime window.occluderFade.enabled
        // toggle) or no player yet: ease any residual fade back and do NO per-frame work (no pool scan).
        // The ticker calls render() every frame, including before a room's map/entities exist.
        if (!this.occluderFade.config.enabled || !player?.location) {
            this.occluderFade.relaxAll()
            this.occluderCandidateBuf.length = 0 // hold no sprite refs while idle
            return
        }
        const feetX = player.location.x
        const feetY = player.location.y + BODY_GROUND_OFFSET
        this.occluderFade.update(feetX, feetY, Mechanics.entity.body.calculateZIndexBoost(player), this.collectOccluderCandidates())
    }

    // Reused buffer of the on-screen fade candidates, refilled each frame (never reallocated) so the
    // fade pass stays allocation-free. Scans only the pool's checked-out slots (0..ptr), not the whole
    // padded pool array; the tag is stamped by CompositeTileRenderer and cleared on pool checkout.
    private occluderCandidateBuf: Sprite[] = []
    private collectOccluderCandidates = (): Sprite[] => {
        const buf = this.occluderCandidateBuf
        buf.length = 0
        const pool = this.spriteManagementService.getSpritePool()
        const items = pool.items
        const ptr = pool.ptr
        for (let i = 0; i <= ptr; i++) {
            const item = items[i]
            if (item && !item.free && item.data && (item.data as any).occluderFade) {
                buf.push(item.data)
            }
        }
        return buf
    }

    // Called on a redrawClient signal (building/chest/door placement). Deferred to renderTileMap so it
    // runs after processPendingDeltas has applied the accompanying deltas. An applied ADD delta sets
    // pendingRedraw itself (see applyMapDelta), for the tiles that arrive without a signal.
    onRedraw = (): void => {
        this.pendingRedraw = true
    }

    // Repaint only the delta-changed cells. Sprited-layer cells are updated in place (remove old owned
    // sprites + re-render the current tile); if any dirty cell belongs to a tilemap/sky/other layer we
    // can't update in isolation (CompositeTilemap is append+clear only), so fall back to a full rerender.
    private repaintDirtyCells = (): void => {
        if (!this.tilemapMetaProvider || this.dirtyCells.size === 0) {
            this.dirtyCells.clear()
            this.dirtyNeedsFull = false
            return
        }
        // Fall back to a FULL rerender when: any dirty batch touched a removal/damaged tile (tile
        // effects -- see dirtyNeedsFull), OR any dirty cell is a non-sprited (tilemap/sky/occlusion)
        // layer that can't be updated in isolation (CompositeTilemap is append+clear only). Otherwise
        // every change is a pristine sprited placement and we repaint just those cells.
        let needFullRerender = this.dirtyNeedsFull
        this.dirtyNeedsFull = false
        this.dirtyCells.forEach(key => {
            const layerId = key.slice(key.lastIndexOf("-") + 1)
            if (this.tilemapMetaProvider.resolveLayerMapping(layerId)?.maptype !== "sprited") {
                needFullRerender = true
            }
        })
        if (needFullRerender) {
            this.dirtyCells.clear()
            this.rerender()
            return
        }
        this.dirtyCells.forEach(key => {
            const dash = key.lastIndexOf("-")
            const layerId = key.slice(dash + 1)
            const [x, y] = key
                .slice(0, dash)
                .split("-")
                .map(Number)
            this.removeSpritedCell(x, y, layerId)
            const tileMap = this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId)
            const tile = tileMap?.[x]?.[y]
            if (tile) {
                this.renderSpritedCell(x, y, tile, layerId, this.spritedLayerIndex(layerId))
            }
        })
        this.dirtyCells.clear()
    }

    queueMapDelta = (delta: MapDelta[]) => {
        this.pendingMapDeltas = this.pendingMapDeltas.concat(delta)
    }

    private processPendingDeltas = () => {
        if (this.pendingMapDeltas.length > 0) {
            const deltas = [...this.pendingMapDeltas]
            this.pendingMapDeltas = []

            deltas.forEach(delta => {
                this.applyMapDelta(delta)
            })
        }
    }

    private applyMapDelta = (delta: MapDelta) => {
        if (!delta || !this.tilemapMetaProvider) {
            return
        }

        const { x, y, blocking, occlusion, tileId, layerId, surfaceId, mapSpriteCap, collideable, temporary, context } =
            delta

        if (tileId) {
            // Remember this cell so a following redrawClient can repaint just it (see repaintDirtyCells).
            this.dirtyCells.add(`${x}-${y}-${layerId}`)
            // A wooden cap's recovered anchor depends on the blocking base below it.
            // Re-evaluate the cap when that base is placed or replaced incrementally.
            if (y > 0 && this.tilemapMetaProvider.resolveLayerMapping(layerId)?.maptype === "sprited") {
                this.dirtyCells.add(`${x}-${y - 1}-${layerId}`)
            }
            // A removal (destroy) or a damaged tile drives the tile-effect system -- keep it on the
            // full-rerender path so crumble/shake/holes are not raced by an incremental remove+recreate.
            if (
                delta.removed ||
                (context?.hp !== undefined && context?.hpMax !== undefined && context.hp < context.hpMax)
            ) {
                this.dirtyNeedsFull = true
            }
            if (delta.removed) {
                this.lavaTileFade.cancel(`${x}-${y}-${layerId}`)
                // The removal delta and the wall's "destroy" tile-effect race; the removal (which drives
                // the surface-clearing rerender below) can win, so the destroy effect then finds no
                // surface to shatter. Snapshot this cell's shatter textures NOW -- the surface + its
                // live sprites still exist at this point -- so a destroy effect arriving after the
                // rerender can still play the shatter from the snapshot.
                const removedSurfaceId = this.spritedCellSurfaceId.get(`${x}-${y}-${layerId}`)
                if (removedSurfaceId) {
                    this.tileEffectSystem.snapshotSurfaceForDestroy(removedSurfaceId)
                }
                this.tilemapMetaProvider.removeTileAt(x, y, layerId)
            } else {
                const layerMapping = this.tilemapMetaProvider.resolveLayerMapping(layerId)
                const tileMap =
                    this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId) ||
                    this.tilemapMetaProvider.entityTileMap()
                const isReplace = layerMapping?.maptype === "tilemap" && tileMap[x][y] !== "0"
                if (layerMapping?.maptype === "tilemap") {
                    const key = `${x}-${y}-${layerId}`
                    const isLava = this.tilemapMetaProvider.resolveTileMeta(tileId).tileTypes.includes("lava")
                    const wasLava = this.tilemapMetaProvider.resolveTileMeta(tileMap[x][y]).tileTypes.includes("lava")
                    if (isLava && !wasLava) this.lavaTileFade.start(key)
                    else if (!isLava) this.lavaTileFade.cancel(key)
                }

                // Incoming server state is authoritative, including increasing HP during repair.
                // Art-only updates may omit health; keep the existing values in that case.
                const existingDelta = this.tilemapMetaProvider.getMapDeltaAt(x, y, layerId)
                const mergedContext = { ...existingDelta?.context,
                    ...Object.fromEntries(Object.entries(context || {}).filter(([, value]) => value !== undefined)),
                }

                this.tilemapMetaProvider.addTileAt(
                    x,
                    y,
                    blocking,
                    occlusion,
                    tileId,
                    layerId,
                    mapSpriteCap,
                    temporary,
                    collideable,
                    mergedContext,
                )

                // Ask for a repaint. A delta that ADDS a tile is the only notice the client gets for
                // a tile that appears with no placement behind it: the sapling a planted seed becomes,
                // the next stage a sapling grows into, a regrown tree. Those all come from
                // ENTITY_TILE_ADDED, which broadcasts the delta and nothing else -- no redrawClient --
                // so without this the cell lands in the data model only (collision/blocking correct,
                // nothing drawn) and is first PAINTED by the next unrelated repaint or a reload.
                //
                // repaintDirtyCells decides how: per-cell on a sprited layer, a full rerender on a
                // tilemap layer (CompositeTilemap is append+clear only, so a cell whose tile was
                // replaced cannot be repainted in isolation) -- which is the entity-tile surface, and
                // where trees/saplings are composite tiles baked by the full render path anyway.
                //
                // Removals and damage are deliberately NOT self-painting: the tile-effect system
                // (crumble, shatter, damage holes) owns those cells and an incremental
                // remove+recreate races it. See dirtyNeedsFull.
                this.pendingRedraw = true

                // if (mapSpriteCap) {
                //     this.tilemapMetaProvider.addMapSpriteCapAt(x, y, mapSpriteCap?.context)
                // }
            }
        } else {
            this.tilemapMetaProvider.updateBlockingAt(x, y, blocking, false)
        }
    }

    // ============================================================================
    // GETTERS
    // ============================================================================

    getVisibilityMap = () => (!this.visibility?.mask ? undefined : this.visibility.mask)
    crossFloorAim = (point: import("game-common/models").Coordinate) =>
        this.sceneRenderer.projectileAim(point, this.tilemapMetaProvider?.twoStoreyHouse)

    getSceneEntity = (id: string) => this.sceneRenderer.getEntity(id)
    getSceneRenderable = (id: string) => this.sceneRenderer.getRenderable(id)
    getSceneLights = () => this.sceneRenderer.getLights()
    updateSceneRooms = (rooms: import("game-common/models").SceneRoomState[]) => {
        this.sceneRenderer.update(rooms, this.tilemapMetaProvider?.getHousePresentation())
        if (this.tilemapMetaProvider) this.sceneRenderer.linkFloorSupport(this.tilemapMetaProvider)
    }

    getVisibility = (): Visibility | undefined => this.visibility

    getCameraCenter = () => this.cameraManagementService.getTranslationComputer().getCameraCenter()

    get translationComputer() {
        return this.cameraManagementService.getTranslationComputer()
    }
}
